import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Clock, Monitor, ArrowRight } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/States";
import { StaggerGroup, StaggerItem } from "@/components/shared/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { fetchCourses, fetchCourseCategories } from "@/lib/coursesApi";
import { resolveMedia } from "@/lib/contentApi";

export default function Courses() {
  useSeo({
    title: "Courses",
    description: "Explore Atmasamman School of AI's industry-ready AI, data science, cloud and DevOps courses.",
    path: "/courses",
  });
  const [all, setAll] = useState(null);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    fetchCourses().then(setAll).catch(() => setAll([]));
    fetchCourseCategories().then(setCats).catch(() => setCats([]));
  }, []);

  const list = (all || []).filter(
    (c) =>
      (cat === "All" || c.category === cat) &&
      (!q ||
        c.name.toLowerCase().includes(q.toLowerCase()) ||
        (c.shortDescription || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div data-testid="page-courses">
      <PageHeader
        overline="Courses"
        titleLines={["Explore Our Courses"]}
        description="Industry-ready AI & technology programs designed to get you hired."
      />
      <section className="bg-white py-12 md:py-16">
        <div className="container-asai">
          <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-navy/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search courses…"
                data-testid="courses-search"
                className="w-full rounded-full border border-navy/15 py-2.5 pl-10 pr-4 text-sm outline-none focus:border-orange"
              />
            </div>
            {cats.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {["All", ...cats].map((x) => (
                  <button
                    key={x}
                    onClick={() => setCat(x)}
                    data-testid={`courses-filter-${x.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                      cat === x ? "bg-orange text-white" : "border border-navy/15 text-navy/70 hover:border-orange"
                    }`}
                  >
                    {x}
                  </button>
                ))}
              </div>
            )}
          </div>

          {all === null ? null : list.length === 0 ? (
            <div className="mt-6">
              <EmptyState title="Courses coming soon" description="We're preparing our course catalogue. Please check back shortly." />
            </div>
          ) : (
            <StaggerGroup className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="courses-grid">
              {list.map((c) => (
                <StaggerItem
                  key={c.id}
                  data-testid="course-card"
                  className="group flex flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-sm transition-shadow duration-300 hover:shadow-xl"
                >
                  <Link to={`/courses/${c.slug}`} className="block aspect-video overflow-hidden bg-lightgrey">
                    {c.thumbnail ? (
                      <img src={resolveMedia(c.thumbnail)} alt={c.name} loading="lazy" className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="flex h-full items-center justify-center font-heading text-4xl text-navy/15">{c.name[0]}</div>
                    )}
                  </Link>
                  <div className="flex flex-1 flex-col p-5">
                    {c.category && <span className="text-xs font-semibold uppercase tracking-wide text-orange">{c.category}</span>}
                    <h3 className="mt-1 font-heading text-xl text-navy">{c.name}</h3>
                    {c.shortDescription && <p className="mt-2 line-clamp-2 text-sm text-navy/60">{c.shortDescription}</p>}
                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-navy/50">
                      {c.duration && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {c.duration}</span>}
                      {c.mode && <span className="inline-flex items-center gap-1"><Monitor className="h-3.5 w-3.5" /> {c.mode}</span>}
                    </div>
                    <Link to={`/courses/${c.slug}`} data-testid="course-view-details" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy transition-colors hover:text-orange">
                      View Details <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>
          )}
        </div>
      </section>
    </div>
  );
}
